import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  FloatButton,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined, SlackOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./UnstractSubscription.css";
import "../../llm-whisperer/components/dashboard/Dashboard.css";
import { IslandLayout } from "../../../layouts/island-layout/IslandLayout";
import { useSessionStore } from "../../../store/session-store";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { useUnstractSubscriptionPlanStore } from "../../store/unstract-subscription-plan-store";
import { useAlertStore } from "../../../store/alert-store";
import { useExceptionHandler } from "../../../hooks/useExceptionHandler";
import useRequestUrl from "../../../hooks/useRequestUrl";

function UnstractUsage() {
  const [usageList, setUsageList] = useState([]);
  const [totalPagesProcessed, setTotalPagesProcessed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { unstractSubscriptionPlan } = useUnstractSubscriptionPlanStore();

  const navigate = useNavigate();
  const { sessionDetails } = useSessionStore();
  const axiosPrivate = useAxiosPrivate();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const { getUrl } = useRequestUrl();

  const SUBSCRIBE_PATH = `/${sessionDetails?.orgName}/pricing`;

  const columns = useMemo(
    () => [
      {
        title: "Number of Pages Extracted",
        dataIndex: "numberOfPagesExtracted",
        key: "numberOfPagesExtracted",
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
      },
    ],
    []
  );

  const fetchUsageData = useCallback(async () => {
    if (!sessionDetails?.orgId) return;
    setIsLoading(true);

    try {
      const requestOptionsUsageDetails = {
        method: "GET",
        url: getUrl("/subscription/usage/"),
      };
      const requestOptionsDailyUsage = {
        method: "GET",
        url: getUrl("/subscription/usage/daily/"),
      };

      // Make requests in parallel
      const [totalUsageRes, dailyUsageRes] = await Promise.all([
        axiosPrivate(requestOptionsUsageDetails),
        axiosPrivate(requestOptionsDailyUsage),
      ]);

      // Process daily usage details
      const dailyUsageDetails = dailyUsageRes?.data?.usage_data?.usage || [];
      const dailyUsageListData = dailyUsageDetails?.map((item) => ({
        key: item?.record_date,
        numberOfPagesExtracted: item?.pages_processed,
        date: item?.record_date,
      }));
      setUsageList(dailyUsageListData);

      // Process total usage details
      const totalUsageDetails = totalUsageRes?.data?.usage_data || {};
      setTotalPagesProcessed(totalUsageDetails?.pages_processed || 0);
    } catch (err) {
      setAlertDetails(handleException(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsageData();
  }, []);

  return (
    <div className="page-bg-2 flex-dir-col">
      <div className="subscription-header display-flex-center display-flex-space-between">
        <Typography.Text className="subscription-font-size" strong>
          Dashboard
        </Typography.Text>
        <Space>
          {unstractSubscriptionPlan?.nextBillingDate &&
            new Date(unstractSubscriptionPlan.nextBillingDate) < new Date() && (
              <Tag color="red">
                Payment overdue. Please pay to avoid service disruption.
              </Tag>
            )}
          {sessionDetails?.isAdmin && !sessionDetails?.provider && (
            <Button type="primary" onClick={() => navigate(SUBSCRIBE_PATH)}>
              Pricing
            </Button>
          )}
          <Button
            icon={<SlackOutlined />}
            type="link"
            onClick={() =>
              window.open("https://join-slack.unstract.com/", "_blank")
            }
          >
            Slack Community
          </Button>
        </Space>
      </div>

      <div className="flex-1 overflow-hidden">
        <IslandLayout>
          <div className="height-100 overflow-y-auto">
            <FloatButton
              className="dash-refresh-button"
              icon={<ReloadOutlined />}
              onClick={fetchUsageData}
            />
            <h3 className="usage-title">Overall Usage</h3>
            <div>
              <Row className="usage-container">
                <Alert
                  message={
                    <strong style={{ margin: "auto" }}>
                      {unstractSubscriptionPlan?.product?.name ||
                        "Subscription not available"}
                    </strong>
                  }
                  description={
                    <Space>
                      <strong className="process-type-title">
                        Total Number of Pages Extracted:
                      </strong>
                      <span className="process-type-value">
                        {totalPagesProcessed}
                      </span>
                    </Space>
                  }
                />
              </Row>
              <div className="width-100 center">
                <div className="usage-table-width">
                  <Table
                    dataSource={usageList}
                    columns={columns}
                    pagination
                    showSorterTooltip={{ target: "sorter-icon" }}
                    size="small"
                    loading={isLoading}
                    bordered
                  />
                </div>
              </div>
            </div>
          </div>
        </IslandLayout>
      </div>
    </div>
  );
}

export { UnstractUsage };
